import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Form, Message } from 'semantic-ui-react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  API,
  copy,
  showError,
  showSuccess,
  timestamp2string,
} from '../../helpers';
import { renderModelQuotaWithPrompt } from '../../helpers/render';

function formatResourceQuota(value) {
  const quota = Number(value || 0);
  if (quota >= 100000000) return `${(quota / 100000000).toFixed(2)} 亿`;
  if (quota >= 10000) return `${(quota / 10000).toFixed(2)} 万`;
  return String(quota);
}

function timestampToDateTimeLocal(timestamp) {
  if (timestamp === -1) return -1;
  return timestamp2string(timestamp).replace(' ', 'T').slice(0, 16);
}

const EditToken = () => {
  const { t } = useTranslation();
  const params = useParams();
  const tokenId = params.id;
  const isEdit = tokenId !== undefined;
  const [loading, setLoading] = useState(isEdit);
  const [modelOptions, setModelOptions] = useState([]);
  const [resourcePackages, setResourcePackages] = useState([]);
  const [resourcePackagesLoading, setResourcePackagesLoading] = useState(false);
  const originInputs = {
    name: '',
    remain_quota: isEdit ? 0 : 500000,
    expired_time: -1,
    unlimited_quota: false,
    resource_package_id: 0,
    models: [],
    subnet: '',
  };
  const [inputs, setInputs] = useState(originInputs);
  const [createdKey, setCreatedKey] = useState('');
  const {
    name,
    remain_quota,
    expired_time,
    unlimited_quota,
    resource_package_id,
  } = inputs;
  const selectedResourcePackageId = Number(resource_package_id || 0);
  const navigate = useNavigate();

  const resourcePackageOptions = useMemo(
    () => [
      {
        key: 0,
        text: '不绑定资源包',
        value: 0,
      },
      ...resourcePackages.map((item) => ({
        key: item.id,
        text: item.plan_name || item.model_name || `资源包 #${item.id}`,
        value: item.id,
        description: `剩余 ${formatResourceQuota(
          item.remaining_quota
        )} Tokens，有效至 ${timestamp2string(item.expired_time)}`,
      })),
    ],
    [resourcePackages]
  );

  const handleInputChange = (e, { name, value }) => {
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  };

  const handleResourcePackageChange = (e, { value }) => {
    const nextId = Number(value || 0);
    const subscription = resourcePackages.find((item) => item.id === nextId);
    if (!nextId || !subscription) {
      setInputs((inputs) => ({
        ...inputs,
        resource_package_id: 0,
      }));
      return;
    }
    setInputs((inputs) => ({
      ...inputs,
      resource_package_id: nextId,
      remain_quota: subscription.remaining_quota,
      unlimited_quota: false,
      expired_time: timestampToDateTimeLocal(subscription.expired_time),
    }));
  };

  const handleCancel = () => {
    navigate('/token');
  };
  const setExpiredTime = (month, day, hour, minute) => {
    let now = new Date();
    let timestamp = now.getTime() / 1000;
    let seconds = month * 30 * 24 * 60 * 60;
    seconds += day * 24 * 60 * 60;
    seconds += hour * 60 * 60;
    seconds += minute * 60;
    if (seconds !== 0) {
      timestamp += seconds;
      setInputs({ ...inputs, expired_time: timestampToDateTimeLocal(timestamp) });
    } else {
      setInputs({ ...inputs, expired_time: -1 });
    }
  };

  const setUnlimitedQuota = () => {
    setInputs({ ...inputs, unlimited_quota: !unlimited_quota });
  };

  const loadToken = async () => {
    try {
      let res = await API.get(`/api/token/${tokenId}`);
      const { success, message, data } = res.data || {};
      if (success && data) {
        if (data.expired_time !== -1) {
          data.expired_time = timestampToDateTimeLocal(data.expired_time);
        }
        data.resource_package_id = data.resource_package_id || 0;
        if (data.models === '') {
          data.models = [];
        } else {
          data.models = data.models.split(',');
        }
        setInputs(data);
      } else {
        showError(message || 'Failed to load token');
      }
    } catch (error) {
      showError(error.message || 'Network error');
    }
    setLoading(false);
  };

  const loadAvailableModels = async () => {
    try {
      let res = await API.get(`/api/user/available_models`);
      const { success, message, data } = res.data || {};
      if (success && data) {
        let options = data.map((model) => {
          return {
            key: model,
            text: model,
            value: model,
          };
        });
        setModelOptions(options);
      } else {
        showError(message || 'Failed to load models');
      }
    } catch (error) {
      showError(error.message || 'Network error');
    }
  };

  const loadResourcePackages = async () => {
    setResourcePackagesLoading(true);
    try {
      let res = await API.get('/api/topup/subscriptions?active_only=true');
      const { success, message, data } = res.data || {};
      if (success) {
        setResourcePackages(data || []);
      } else {
        showError(message || 'Failed to load resource packages');
      }
    } catch (error) {
      showError(error.message || 'Network error');
    } finally {
      setResourcePackagesLoading(false);
    }
  };

  useEffect(() => {
    if (isEdit) {
      loadToken().catch((error) => {
        showError(error.message || 'Failed to load token');
        setLoading(false);
      });
    }
    loadAvailableModels().catch((error) => {
      showError(error.message || 'Failed to load models');
    });
    loadResourcePackages().catch((error) => {
      showError(error.message || 'Failed to load resource packages');
    });
  }, []);

  const submit = async () => {
    if (!isEdit && inputs.name === '') return;
    let localInputs = {
      ...inputs,
      remain_quota: parseInt(inputs.remain_quota),
      resource_package_id: parseInt(inputs.resource_package_id || 0),
      models: Array.isArray(inputs.models)
        ? inputs.models.join(',')
        : inputs.models || '',
    };
    if (localInputs.expired_time !== -1 && localInputs.expired_time !== '-1') {
      let time = Date.parse(localInputs.expired_time);
      if (isNaN(time)) {
        showError(t('token.edit.messages.expire_time_invalid'));
        return;
      }
      localInputs.expired_time = Math.ceil(time / 1000);
    } else {
      localInputs.expired_time = -1;
    }
    let res;
    if (isEdit) {
      res = await API.put(`/api/token/`, {
        ...localInputs,
        id: parseInt(tokenId),
      });
    } else {
      res = await API.post(`/api/token/`, localInputs);
    }
    const { success, message, data } = res.data;
    if (success) {
      if (isEdit) {
        showSuccess(t('token.edit.messages.update_success'));
      } else {
        showSuccess(t('token.edit.messages.create_success'));
        setCreatedKey(data?.key || '');
        setInputs(originInputs);
      }
    } else {
      showError(message);
    }
  };

  return (
    <div className='dashboard-container'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header className='header'>
            {isEdit ? t('token.edit.title_edit') : t('token.edit.title_create')}
          </Card.Header>
          <Form loading={loading} autoComplete='new-password'>
            {!isEdit && createdKey ? (
              <Message positive>
                <Message.Header>令牌已创建，请立即复制保存</Message.Header>
                <p>出于安全考虑，令牌明文仅展示一次，离开本页后后台不会再返回完整 key。</p>
                <code style={{ wordBreak: 'break-all' }}>
                  {createdKey.startsWith('sk-') ? createdKey : `sk-${createdKey}`}
                </code>
                <div style={{ marginTop: 12 }}>
                  <Button
                    type='button'
                    size='small'
                    primary
                    onClick={() => copy(createdKey.startsWith('sk-') ? createdKey : `sk-${createdKey}`)}
                  >
                    复制令牌
                  </Button>
                </div>
              </Message>
            ) : null}
            <Form.Field>
              <Form.Input
                label={t('token.edit.name')}
                name='name'
                placeholder={t('token.edit.name_placeholder')}
                onChange={handleInputChange}
                value={name}
                autoComplete='new-password'
                required={!isEdit}
              />
            </Form.Field>
            <Form.Field>
              <Form.Dropdown
                label={t('token.edit.models')}
                placeholder={t('token.edit.models_placeholder')}
                name='models'
                fluid
                multiple
                search
                onLabelClick={(e, { value }) => {
                  copy(value).then();
                }}
                selection
                onChange={handleInputChange}
                value={inputs.models}
                autoComplete='new-password'
                options={modelOptions}
              />
            </Form.Field>
            <Form.Field>
              <Form.Input
                label={t('token.edit.ip_limit')}
                name='subnet'
                placeholder={t('token.edit.ip_limit_placeholder')}
                onChange={handleInputChange}
                value={inputs.subnet}
                autoComplete='new-password'
              />
            </Form.Field>
            <Form.Field>
              <Form.Input
                label={t('token.edit.expire_time')}
                name='expired_time'
                placeholder={t('token.edit.expire_time_placeholder')}
                onChange={handleInputChange}
                value={expired_time}
                autoComplete='new-password'
                type='datetime-local'
              />
            </Form.Field>
            <div style={{ lineHeight: '40px' }}>
              <Button
                type={'button'}
                onClick={() => {
                  setExpiredTime(0, 0, 0, 0);
                }}
              >
                {t('token.edit.buttons.never_expire')}
              </Button>
              <Button
                type={'button'}
                onClick={() => {
                  setExpiredTime(1, 0, 0, 0);
                }}
              >
                {t('token.edit.buttons.expire_1_month')}
              </Button>
              <Button
                type={'button'}
                onClick={() => {
                  setExpiredTime(0, 1, 0, 0);
                }}
              >
                {t('token.edit.buttons.expire_1_day')}
              </Button>
              <Button
                type={'button'}
                onClick={() => {
                  setExpiredTime(0, 0, 1, 0);
                }}
              >
                {t('token.edit.buttons.expire_1_hour')}
              </Button>
              <Button
                type={'button'}
                onClick={() => {
                  setExpiredTime(0, 0, 0, 1);
                }}
              >
                {t('token.edit.buttons.expire_1_minute')}
              </Button>
            </div>
            <Message>{t('token.edit.quota_notice')}</Message>
            <Form.Field>
              <Form.Input
                label={`${t('token.edit.quota')}${renderModelQuotaWithPrompt(
                  remain_quota,
                  t,
                  inputs.models,
                  modelOptions
                )}`}
                name='remain_quota'
                placeholder={t('token.edit.quota_placeholder')}
                onChange={handleInputChange}
                value={remain_quota}
                autoComplete='new-password'
                type='number'
                disabled={unlimited_quota || selectedResourcePackageId > 0}
              />
            </Form.Field>
            <Form.Field>
              <Form.Dropdown
                label='账户资源包'
                name='resource_package_id'
                placeholder='选择已订阅资源包'
                fluid
                search
                selection
                clearable
                loading={resourcePackagesLoading}
                onChange={handleResourcePackageChange}
                value={selectedResourcePackageId}
                options={resourcePackageOptions}
                noResultsMessage='暂无可用资源包'
              />
            </Form.Field>
            <Button
              type={'button'}
              disabled={selectedResourcePackageId > 0}
              onClick={() => {
                setUnlimitedQuota();
              }}
            >
              {unlimited_quota
                ? t('token.edit.buttons.cancel_unlimited')
                : t('token.edit.buttons.unlimited_quota')}
            </Button>
            <Button floated='right' positive onClick={submit}>
              {t('token.edit.buttons.submit')}
            </Button>
            <Button floated='right' onClick={handleCancel}>
              {t('token.edit.buttons.cancel')}
            </Button>
          </Form>
        </Card.Content>
      </Card>
    </div>
  );
};

export default EditToken;
